// The sample racing card /demo and the sample board run on.
//
// Built relative to "now" so it always has races still to jump, and built in
// PUNTERSEDGE'S OWN SHAPE rather than in the app's — so it goes through exactly
// the code a real card does (`fromRaces` in lib/racing/board.js, then
// `buildUniverse`). That is the same rule lib/demo.js follows for the sports
// board, and for the same reason: a demo that skips the ingest is a demo that
// cannot fail when the ingest breaks.
//
// Six races, three codes, two horse meetings, spaced from ten minutes ago to
// three hours out, with fourteen bookmakers on the near ones. Four things are
// planted in it, and each is named where it is planted:
//
//   the SCRATCHING   Randwick R5's number 7, in `scratchings` and therefore
//                    never in `runners` — which is what the upstream does, and
//                    what keeps a scratched runner out of `selections`.
//   the MISPRICED    Randwick R5's number 4: Neds pays TWENTY-SEVEN per cent
//                    more than the rest of the board, which is about six points
//                    over the devigged fair price and the figure the detector
//                    actually reads. Every one of these plants has been raised
//                    twice, and both times for the same reason: a percentage
//                    over the raw BOARD is not a finding. Six per cent was
//                    under fair (a fourteen-runner round is ~117%); twenty per
//                    cent cleared the floor under Shin and power and not under
//                    multiplicative, and since engine review #3 a leg has to
//                    clear it under all three (`CERTAINTY_METHODS`, devig.js).
//                    A plant that only exists under the friendliest arithmetic
//                    would teach the demo's reader the opposite of the rule.
//                    Racing's floor is RACING_MIN_EDGE on RACING_MIN_BOOKS.
//   the DUTCH        Flemington R7: Ladbrokes long on number 1, PointsBet long
//                    on number 2, and the best price on every runner adds to
//                    less than a whole — "Covered".
//   the VALUE PAIR   Menangle R4's number 1 and Randwick R6's number 1, both at
//                    SPORTSBET — which is the profile's own book as the app
//                    ships. Racing value measures the price the punter can
//                    actually get against the rest of the card, so a card where
//                    every book agrees gives it nothing to find and the demo
//                    would teach that the strategy is broken. Nineteen per cent
//                    each, because the engine's edge is in PROBABILITY POINTS:
//                    a fourteen-runner market carries so much margin that six
//                    per cent over the odds is barely two points, `minEdge` is
//                    three, and the points have to survive all three devigs.
//                    The two late races, so the pair can never collide with the
//                    two plants above.
//
// Everything else on the card is tight, which is what makes those four the only
// things any detector can find.
//
// Prices are written out as decimal odds, in one table per race, rather than
// generated: the whole value of a fixture is being able to read what it says.

/** The fourteen books PuntersEdge quotes on an Australian race. */
export const DEMO_BOOKS = [
  'sportsbet', 'tab', 'ladbrokes_au', 'neds', 'pointsbetau', 'unibet', 'betr_au',
  'tabtouch', 'playup', 'betright', 'palmerbet', 'betdeluxe', 'boostbet', 'betgold',
];

/**
 * Each book's own shade on the whole card, as a multiplier.
 *
 * A flat multiplier, not a per-runner wobble, and that is what makes the card
 * readable: it moves a book's OVERROUND without changing the shape of its
 * opinion, so every book devigs to the same fair probabilities and the two
 * things deliberately out of line below are the only two the detectors can
 * find. A random jitter would have sprayed false flags across six races.
 */
const BOOK_TILT = {
  sportsbet: 1.012,
  tab: 0.994,
  ladbrokes_au: 1.006,
  neds: 1.004,
  pointsbetau: 1.000,
  unibet: 0.998,
  betr_au: 1.010,
  tabtouch: 0.992,
  playup: 0.988,
  betright: 1.008,
  palmerbet: 1.002,
  betdeluxe: 0.996,
  boostbet: 1.014,
  betgold: 0.990,
};

/**
 * The card. One entry per race:
 *
 *   minutes   from now; negative has already jumped
 *   runners   [number, name, barrier, rider, trainer, form, price]
 *   books     how many of DEMO_BOOKS quote it — depth grows towards the jump,
 *             which is what a real card does
 *   overrides { bookKey: { runnerNumber: price } } — the planted prices
 */
const CARD = [
  {
    id: 'demo-race-angle-park-8',
    venue: 'Angle Park',
    venueId: 'angle-park',
    number: 8,
    category: 'greyhound',
    minutes: -10,
    jumped: true,
    name: 'SKY RACING MAIDEN STAKE',
    distance: 395,
    condition: 'Good',
    places: 3,
    books: 14,
    runners: [
      [1, 'Mrs Puddleduck', 1, null, 'J Formosa', '1-2-1', 3.20],
      [2, 'Mustang Carlos', 2, null, 'C Fisher', '4-3-2', 4.50],
      [3, 'Clonkeen Terry', 3, null, 'R Bewley', '2-1-5', 5.00],
      [4, 'Winterfield Rain', 4, null, 'K Bell', '3-5-4', 7.00],
      [5, 'Peace O Mind', 5, null, 'A Langton', '6-4-3', 9.00],
      [6, 'Zipping Garth', 6, null, 'M Delbridge', '5-6-6', 12.00],
      [7, 'Sunset Junior', 7, null, 'T Britton', '7-7-5', 15.00],
      [8, 'Hasten Slowly', 8, null, 'J Formosa', '8-8-7', 21.00],
    ],
  },
  {
    id: 'demo-race-wentworth-park-3',
    venue: 'Wentworth Park',
    venueId: 'wentworth-park',
    number: 3,
    category: 'greyhound',
    minutes: 15,
    name: 'LADBROKES FAST WITHDRAWALS',
    distance: 520,
    condition: 'Good',
    places: 3,
    books: 14,
    // THE ONE RACE ON THE SAMPLE CARD THE EXCHANGE IS ON, and it is the quiet
    // race on purpose: what it demonstrates is the yardstick, not a finding.
    // The prices are the field's own fair prices with a two per cent exchange
    // round on them and the commission added back — so `bookFairNet` deflates
    // them to the board's own opinion and nothing here is out of line with
    // anything. They are LONGER than every bookmaker's, which is what an
    // exchange price is: the round is 2% against the books' 21%, and reading
    // them gross would make the field look covered. `arbsFor` deflates before
    // it asks, which is why it is not.
    //
    // Fifteen minutes out with no traded volume on it, which is exactly what a
    // DELAYED Betfair key gives a real device: the trust gate refuses it and
    // the race page says the fair price is the local books. That is the state
    // the owner's phone is in all afternoon, so it is the state the sample
    // shows.
    exchange: { 1: 2.94, 2: 4.90, 3: 7.40, 4: 10.50, 5: 13.50, 6: 17.50, 7: 22.00, 8: 32.00 },
    // The greyhound model's own numbers on the same field. Iggy rates the dogs
    // and Kash the gallops; nobody rates the trots, which is why Menangle R4
    // carries none and the race page there says nothing rather than a blank.
    ratings: {
      1: [2.55, 0.61], 2: [4.10, 0.44], 3: [5.80, 0.36], 4: [9.00, 0.28],
      5: [10.50, 0.21], 6: [15.00, 0.17], 7: [19.00, 0.12], 8: [28.00, 0.08],
    },
    runners: [
      [1, 'Harbour Lights', 1, null, 'P Cassar', '1-1-2', 2.40],
      [2, 'Glebe Point', 2, null, 'D Pringle', '2-3-1', 4.00],
      [3, 'Tempo Fury', 3, null, 'R Colsen', '3-2-4', 6.00],
      [4, 'Blackwattle Bay', 4, null, 'S Elliott', '5-4-3', 8.50],
      [5, 'Pyrmont Prince', 5, null, 'J Galea', '4-6-5', 11.00],
      [6, 'Ultimo Belle', 6, null, 'K Lee', '6-5-6', 14.00],
      [7, 'Broadway Star', 7, null, 'M Azzopardi', '7-8-7', 18.00],
      [8, 'Jubilee Miss', 8, null, 'P Cassar', '8-7-8', 26.00],
    ],
  },
  {
    id: 'demo-race-randwick-5',
    venue: 'Randwick',
    venueId: 'randwick',
    number: 5,
    category: 'horse',
    minutes: 40,
    name: 'BENCHMARK 78 HANDICAP',
    distance: 1200,
    condition: 'Good (4)',
    places: 3,
    books: 14,
    runners: [
      [1, 'Harbour Bridge', 3, 'J McDonald', 'C Waller', '2-1-3', 4.60],
      [2, 'Coogee Dancer', 8, 'N Rawiller', 'A Cummings', '1-4-2', 7.00],
      [3, 'Kensington Kid', 11, 'T Berry', 'J Thompson', '5-3-6', 10.00],
      [4, 'Southern Cross', 5, 'K McEvoy', 'G Waterhouse', '1-1-2', 2.80],
      [5, 'Doncaster Mile', 1, 'R King', 'B Baker', '3-2-1', 6.00],
      [6, 'Queen Of Spades', 9, 'J Parr', 'M Freedman', '6-5-4', 12.00],
      [8, 'Paddington Lad', 2, 'B Shinn', 'P Perry', '7-8-5', 17.00],
      [9, 'Alison Road', 12, 'T Clark', 'K Lees', '8-7-9', 21.00],
    ],
    // Scratched at the barrier. It is in `scratchings` and nowhere else.
    scratchings: [[7, 'Bondi Junction', 7, '2026-09-15T00:41:00Z']],
    // THE HUB'S OWN RATED PRICES, as a SECOND OPINION and nothing else.
    //
    // They are close to the board's own prices and they disagree about one
    // runner — the Hub has Southern Cross at 3.10 where the market has come in
    // to 2.80 — which is the honest shape of a free model: mostly agreeing,
    // occasionally not, and never the reason anything on this screen is
    // printed. Nothing reads them but the line under the runner's name.
    ratings: {
      1: [4.40, 0.42], 2: [7.60, 0.31], 3: [9.20, 0.18], 4: [3.10, 0.77],
      5: [6.20, 0.55], 6: [13.50, 0.22], 8: [16.00, 0.10], 9: [24.00, 0.06],
    },
    // THE MISPRICED RUNNER. Neds is paying 3.55 where the rest of the board
    // has come in to 2.80 — twenty-seven per cent over the board, which is
    // about six points over the fair price of the thirteen books that are not
    // Neds, under every one of the three devigs a leg has to survive. One
    // book, one runner, and the shop that has not moved with the money (see the
    // mover below: this is the same runner the other thirteen firmed).
    overrides: { neds: { 4: 3.55 } },
  },
  {
    id: 'demo-race-flemington-7',
    venue: 'Flemington',
    venueId: 'flemington',
    number: 7,
    category: 'horse',
    minutes: 95,
    name: 'GROUP 3 SIRES PRODUCE STAKES',
    distance: 1400,
    condition: 'Soft (5)',
    places: 3,
    books: 14,
    runners: [
      [1, 'Maribyrnong Miss', 4, 'D Oliver', 'C Maher', '1-2-1', 2.70],
      [2, 'Ascot Vale Boy', 2, 'C Williams', 'M Price', '2-1-4', 3.30],
      [3, 'Newmarket Nell', 7, 'J Allen', 'D Weir', '3-5-2', 5.50],
      [4, 'Straight Six', 1, 'B Melham', 'A Freedman', '4-3-5', 8.00],
      [5, 'Rails Run', 9, 'M Zahra', 'P Moody', '6-4-3', 12.00],
      [6, 'Clocktower', 5, 'L Nolen', 'R Griffiths', '7-6-8', 23.00],
    ],
    // THE DUTCH. Two books long enough on the two at the head of the market
    // that the best price on every runner adds to less than a whole — back the
    // lot and the round is covered.
    overrides: { ladbrokes_au: { 1: 3.40 }, pointsbetau: { 2: 4.10 } },
  },
  {
    id: 'demo-race-menangle-4',
    venue: 'Menangle',
    venueId: 'menangle',
    number: 4,
    category: 'harness',
    minutes: 150,
    name: 'GARRARDS HORSE AND HOUND PACE',
    distance: 1609,
    condition: 'Good',
    places: 3,
    books: 12,
    runners: [
      [1, 'Menangle Magic', null, 'L McCarthy', 'B McCarthy', '1-1-3', 2.10],
      [2, 'Sulky Sam', null, 'C Hart', 'E Kennedy', '2-3-1', 4.20],
      [3, 'Pacing Poet', null, 'J McCarthy', 'K Rushton', '3-2-4', 6.50],
      [4, 'Trotting Tom', null, 'T Jackson', 'M Watson', '5-4-2', 9.00],
      [5, 'Gig Harbour', null, 'R Brown', 'S Dawson', '4-6-5', 13.00],
      [6, 'Nightfall Ned', null, 'A Turnbull', 'G Hall', '6-5-7', 19.00],
      [7, 'Camden Clipper', null, 'M Turnbull', 'B McCarthy', '7-7-6', 29.00],
    ],
    // HALF THE VALUE PAIR. Sportsbet nineteen per cent over the card on the
    // pacer at the head of the market — the kind of top fluctuation one book
    // will hold while the other thirteen have come in.
    overrides: { sportsbet: { 1: 2.50 } },
    // AND THE ONE RACE ON THE SAMPLE CARD WITH A BOOK'S OWN PAGE ON IT.
    //
    // `source_url` is what PuntersEdge writes per book per runner, and it is
    // written here for ONE book on ONE race for the same reason every other
    // plant on this card is one of a kind: the demo has to show both halves.
    // Sportsbet because Sportsbet is the value plant above and therefore the
    // book this race's ticket names, so "Open at Sportsbet" on it opens this
    // URL while every other ticket on the pad still opens a front door.
    links: { sportsbet: 'https://www.sportsbet.com.au/betting/harness-racing/australia-nz/menangle/race-4-9364821' },
  },
  {
    id: 'demo-race-randwick-6',
    venue: 'Randwick',
    venueId: 'randwick',
    number: 6,
    category: 'horse',
    minutes: 180,
    name: 'THE SYDNEY STAKES',
    distance: 1600,
    condition: 'Good (4)',
    places: 3,
    books: 10,
    runners: [
      [1, 'Centennial Park', 6, 'J McDonald', 'C Waller', '1-3-1', 2.90],
      [2, 'Anzac Parade', 3, 'T Berry', 'J Cummings', '2-2-3', 4.00],
      [3, 'Moore Park', 10, 'N Rawiller', 'G Waterhouse', '4-1-2', 5.20],
      [4, 'Botany Bay', 1, 'K McEvoy', 'B Baker', '3-4-5', 7.50],
      [5, 'La Perouse', 8, 'B Shinn', 'M Freedman', '5-6-4', 10.00],
      [6, 'Maroubra Morn', 4, 'J Parr', 'K Lees', '6-5-7', 15.00],
      [7, 'Clovelly Lass', 12, 'R King', 'P Perry', '8-7-6', 23.00],
    ],
    // The other half, on a longer price: the same nineteen per cent is worth
    // fewer POINTS out here than it is on a 2.10 favourite, which is the whole
    // reason this app measures both an edge and an EV.
    overrides: { sportsbet: { 1: 3.45 } },
  },
];

/**
 * WHAT THE MONEY DID, on the same six races — the sample answer to
 * `GET /api/racing/movers`.
 *
 * Three rows, and the third is there to be thrown away:
 *
 *   the STEAMER   Randwick R5's number 4, in from 3.20 to 2.80 on five books.
 *                 The same runner Neds is still paying 3.55 about, which is why
 *                 that race is both the demo's mispriced runner and its steamer:
 *                 one shop that has not moved with the other thirteen is what a
 *                 mispriced racing price usually IS.
 *   the DRIFTER   Randwick R6's number 2, out from 3.60 to 4.00 on four.
 *   the REFUSED   Menangle R4's number 1, in twelve per cent — on TWO books.
 *                 `moversByRace` drops it (MOVER_MIN_BOOKS is three), and it is
 *                 in the fixture so that the demo carries a row the filter has
 *                 to refuse rather than only rows it accepts.
 *
 * `move_pct` is NEGATIVE for a firming runner, exactly as the feed publishes it
 * — the price got shorter. Nothing flips the sign; `direction` is the field the
 * app branches on.
 */
const MOVERS = [
  {
    race: 'demo-race-randwick-5',
    number: 4,
    runner: 'Southern Cross',
    direction: 'firming',
    open: 3.20,
    current: 2.80,
    movePct: -12.5,
    books: 5,
  },
  {
    race: 'demo-race-randwick-6',
    number: 2,
    runner: 'Anzac Parade',
    direction: 'drifting',
    open: 3.60,
    current: 4.00,
    movePct: 11.11,
    books: 4,
  },
  {
    race: 'demo-race-menangle-4',
    number: 1,
    runner: 'Menangle Magic',
    direction: 'firming',
    open: 2.40,
    current: 2.10,
    movePct: -12.5,
    books: 2,
  },
];

/**
 * The sample movers, in the shape `GET /api/racing/movers` answers.
 *
 * Built off the same `CARD` the races are, so a mover's venue, code, race number
 * and start time are the race's own — which is the whole of how `moversByRace`
 * joins the two (it rebuilds the race's `matchKey` from those four).
 */
export function demoRaceMovers(nowMs = Date.now()) {
  return MOVERS.map((m) => {
    const race = CARD.find((r) => r.id === m.race);
    const start = new Date(nowMs + race.minutes * 60_000).toISOString();
    return {
      venue: race.venue,
      race_number: race.number,
      category: race.category,
      country: 'AU',
      start_time: start,
      mins_to_jump: race.minutes,
      runner: m.runner,
      number: m.number,
      direction: m.direction,
      open_price: m.open,
      current_price: m.current,
      move_pct: m.movePct,
      books_quoting: race.books,
      books_with_history: race.books,
      books_firming: m.direction === 'firming' ? m.books : 0,
      books_drifting: m.direction === 'drifting' ? m.books : 0,
      books_unchanged: race.books - m.books,
      bookmakers: [],
    };
  });
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * The sample's own Betfair Hub ratings, in the shape
 * `GET /api/racing/features` answers — so the join on the demo card goes
 * through exactly the code a real day's ratings go through
 * (`attachRatings` in lib/racing/ratings.js), venue matcher and all.
 *
 * Two races of the six carry them, which is roughly what a real day looks
 * like: the Hub rates the gallops and the dogs, not the trots, and not every
 * meeting.
 */
export function demoRaceFeatures(nowMs = Date.now()) {
  const sports = {};
  for (const race of CARD) {
    if (!race.ratings) continue;
    const sport = race.category === 'greyhound' ? 'racing_greyhound' : 'racing_horse';
    const model = sport === 'racing_greyhound' ? 'iggy-joey' : 'kash-ratings-model';
    const entry = sports[sport] ?? { model, races: [] };
    entry.races.push({
      venue: race.venueId,
      race: race.number,
      sport,
      runners: Object.entries(race.ratings).map(([cloth, [ratedPrice, earlySpeed]]) => ({
        cloth: Number(cloth),
        name: race.runners.find(([n]) => n === Number(cloth))?.[1] ?? `No. ${cloth}`,
        ratedPrice,
        earlySpeed,
      })),
    });
    sports[sport] = entry;
  }
  return {
    forDate: new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }),
    fetchedAt: new Date(nowMs).toISOString(),
    sports,
  };
}

/**
 * The sample card, in the shape `GET /api/racing` answers.
 *
 * `{ forDate, fetchedAt, races: [NextRaceOut…], credits }` — so the store can
 * assign it straight into `racingSnapshot` and every screen below reads it
 * exactly as it reads a real one.
 */
export function demoRaceCard(nowMs = Date.now()) {
  const at = (minutes) => new Date(nowMs + minutes * 60_000).toISOString();
  const races = CARD.map((race) => {
    const books = DEMO_BOOKS.slice(0, race.books);
    const start = at(race.minutes);
    return {
      race_id: race.id,
      venue: race.venue,
      venue_id: race.venueId,
      venue_canonical: race.venue,
      venue_site: race.venueId,
      race_number: race.number,
      category: race.category,
      start_time: start,
      country: 'AU',
      race_name: race.name,
      distance_m: race.distance,
      track_condition: race.condition,
      places_paid: race.places,
      // A race that has jumped says so from the feed, not from the clock: the
      // board dims it on `inplay_at` whatever the time on the phone says.
      inplay_at: race.jumped ? at(race.minutes) : null,
      market_closed_at: race.jumped ? at(race.minutes - 1) : null,
      market_state_source: race.jumped ? 'pointsbetau' : null,
      refresh_tier: race.minutes < 180 ? 'live' : 'card',
      stale: false,
      stale_bookmakers: [],
      scratchings: (race.scratchings ?? []).map(([number, name, barrier, scratchedAt]) => ({
        number, name, barrier, scratched_at: scratchedAt,
      })),
      runners: race.runners.map(([number, name, barrier, jockey, trainer, form, price]) => ({
        number,
        name,
        barrier,
        jockey,
        trainer,
        weight: race.category === 'horse' ? 56.5 : null,
        form,
        bookmakers: [
          // The exchange is one more column on the card, exactly as it is on a
          // real one (`exchangeRows` in lib/racing/exchange.js): same row shape,
          // `betfair_ex_au` in the book field, the price GROSS of commission.
          ...(race.exchange?.[number]
            ? [{ key: 'betfair_ex_au', win_price: race.exchange[number], age_seconds: 12, stale: false, refresh_tier: 'live' }]
            : []),
          ...books.map((key) => ({
          key,
          win_price: round2(race.overrides?.[key]?.[number] ?? price * BOOK_TILT[key]),
          age_seconds: 12,
          stale: false,
          refresh_tier: 'live',
          // The book's own page for the race, where the feed carries one. It is
          // per book per runner upstream and the same URL for every runner in
          // the race, which is what a race page is — see `links` on the card.
          ...(race.links?.[key] ? { source_url: race.links[key] } : {}),
          })),
        ],
      })),
    };
  });

  return {
    forDate: new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }),
    fetchedAt: new Date(nowMs).toISOString(),
    races,
    // The sample has no key behind it, so it has no meter either. Null rather
    // than an invented balance: a number here would be a number somebody
    // checked against their own dashboard and could not find.
    credits: null,
  };
}
